import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Feedback {
  id: string
  customer: string
  type: string
  channel: string
  sentiment: string
  text: string
  date: string
  status: string
  priority: string
}

interface Campaign {
  id: string
  name: string
  type: string
  status: string
  responses: number
  response_rate: number
  avg_sentiment: number
  start_date: string
}

interface Insight {
  id: string
  title: string
  description: string
  category: string
  impact: string
  feedback_count: number
  action_status: string
}

interface Statistics {
  total_feedback: number
  positive_percentage: number
  response_rate: number
  actionable_insights: number
  active_campaigns: number
  avg_sentiment: number
}

export default function FeedbackManagementPage() {
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [insights, setInsights] = useState<Insight[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [feedbackRes, campaignsRes, insightsRes, statsRes] = await Promise.all([
        fetch('/api/feedbackmanagement/feedback'),
        fetch('/api/feedbackmanagement/campaigns'),
        fetch('/api/feedbackmanagement/insights'),
        fetch('/api/feedbackmanagement/statistics')
      ])
      if (feedbackRes.ok) setFeedback(await feedbackRes.json())
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json())
      if (insightsRes.ok) setInsights(await insightsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching feedback management data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSentimentBadge = (sentiment: string) => {
    if (sentiment === 'positive') return 'bg-green-100 text-green-800'
    if (sentiment === 'negative') return 'bg-red-100 text-red-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active' || status === 'completed' || status === 'implemented') return 'bg-green-100 text-green-800'
    if (status === 'in_progress' || status === 'pending') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getImpactBadge = (impact: string) => {
    if (impact === 'high') return 'bg-red-100 text-red-800'
    if (impact === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getSentimentColor = (score: number) => {
    if (score >= 7) return 'text-green-600'
    if (score >= 4) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Feedback Management</h1>
          <p className="mt-2 text-gray-600">Phase 228 - Centralized feedback collection and analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Feedback</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_feedback}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Positive</p>
              <p className="text-2xl font-bold text-green-600">{statistics.positive_percentage}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Sentiment</p>
              <p className={`text-2xl font-bold ${getSentimentColor(statistics.avg_sentiment)}`}>
                {statistics.avg_sentiment}/10
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Response Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.response_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Insights</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.actionable_insights}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Campaigns</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.active_campaigns}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Feedback</h2>
          </div>
          <div className="p-6 space-y-4">
            {feedback.map((fb) => (
              <div key={fb.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{fb.customer}</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{fb.type}</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{fb.channel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${getSentimentBadge(fb.sentiment)}`}>
                      {fb.sentiment}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(fb.status)}`}>
                      {fb.status}
                    </span>
                    <span className="text-gray-500 text-sm">{fb.date}</span>
                  </div>
                </div>
                <p className="text-gray-700">{fb.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Feedback Campaigns</h2>
            </div>
            <div className="p-6 space-y-4">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{campaign.name}</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{campaign.type}</span>
                    <span className="text-gray-500">Started: {campaign.start_date}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Responses</p>
                      <p className="font-medium text-gray-900">{campaign.responses}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Rate</p>
                      <p className="font-medium text-blue-600">{campaign.response_rate}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Sentiment</p>
                      <p className={`font-medium ${getSentimentColor(campaign.avg_sentiment)}`}>
                        {campaign.avg_sentiment}/10
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Actionable Insights</h2>
            </div>
            <div className="p-6 space-y-4">
              {insights.map((insight) => (
                <div key={insight.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{insight.title}</span>
                    <span className={`px-2 py-1 rounded text-xs ${getImpactBadge(insight.impact)}`}>
                      {insight.impact} impact
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{insight.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{insight.category}</span>
                      <span className="text-gray-500">{insight.feedback_count} feedback items</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(insight.action_status)}`}>
                      {insight.action_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
